'use client';

import type {
    FormStatusData,
    PaymentIntentData,
    PurchaseData,
} from '@lib/types';
import { JSX, useState, useMemo, useCallback, useEffect } from 'react';
import GiftCardPurchaseSelection from './GiftCardPurchaseSelection';
import style from './PurchaseFlow.module.scss';
import PurchaserDetailsComponent from './PurchaserDetailsComponent';
import RecipientDetailsComponent from './RecipientDetailsComponent';
import GiftCardSummary from './GiftCardSummary';
import CreatePaymentIntent from '@lib/stripe/createPaymentIntent';
import StripeCardPurchaseComponent from './StripeCardPurchaseComponent';
import LoadingSpinner from '@components/LoadingSpinner';
import StripeCardElement from '@components/StripeElements/StripeCardElement';
import { useSearchParams } from 'next/navigation';
import logPurchaseData from '@lib/serverActions/logPurchaseData';
import Button from '@components/SectionComponents/Buttons/Button';
import LinkComponent from '@components/SectionComponents/LinkComponent';

const PurchaseFlow = () => {
    // Loading spinner state
    const [isLoading, setIsLoading] = useState(false);

    // Purchase Data default state
    const [purchaseData, setPurchaseData] = useState<PurchaseData>({
        amount: 0,
        purchaserDetails: {
            purchaserName: '',
            purchaserEmail: '',
            purchaserPhone: '',
        },
        gift: false,
        recipientDetails: {
            recipientName: '',
            recipientEmail: '',
            senderName: '',
            message: '',
        },
    });

    // Payment Intent data default state
    const [paymentIntent, setPaymentIntent] = useState<PaymentIntentData>({
        paymentIntentId: '',
        clientSecret: '',
        fromParams: false,
    });

    // Form status data default state
    const [formStatusData, setFormStatusData] = useState<FormStatusData>({
        message: '',
        show: false,
        colour: 'red',
    });

    // Payment status default state
    const [paymentStatus, setPaymentStatus] = useState<
        'incomplete' | 'complete'
    >('incomplete');

    // Current payment flow step state
    const [currentStepKey, setCurrentStepKey] =
        useState<string>('giftCardSelection');

    // Setup to get search params from URL
    const searchParams = useSearchParams();

    /**
     * Skip to stripePayment step if client secret is returned in URL
     */
    useEffect(() => {
        // get the payment_intent_client_secret from the URL
        const returnedClientSecret = searchParams.get(
            'payment_intent_client_secret',
        );

        // get the payment_intent from the URL
        const returnedPaymentIntentId = searchParams.get('payment_intent');

        // if client secret is returned, set the payment intent and skip to stripePayment step
        if (returnedClientSecret && returnedPaymentIntentId) {
            setPaymentIntent({
                paymentIntentId: returnedPaymentIntentId,
                clientSecret: returnedClientSecret,
                fromParams: true,
            });

            setCurrentStepKey('stripePayment');

            // Show loading spinner while payment intent is being processed
            setIsLoading(true);

            const element = document.getElementById('gift');
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
            return;
        }
    }, [searchParams]);

    /**
     * Send data to server action to generate a payment intent on the server.
     * Ideally this is done as early as possible so it happens in the background.
     */
    const processServerActionPaymentIntent = useCallback(async () => {
        try {
            // attempt to create a payment intent with server action
            const { paymentIntentId, clientSecret, error } =
                await CreatePaymentIntent(purchaseData.amount);

            // if it returns an error, log it and throw an error
            if (error) {
                console.error('Server action failed:', error);
                throw new Error(error);
            }

            // if no error, update the paymentIntent data
            setPaymentIntent({
                paymentIntentId: paymentIntentId,
                clientSecret: clientSecret,
                fromParams: false,
            });
            
        } catch (error) {
            console.error('Server action failed:', error);
            // Handle error
            // this is something I am trying, might not work well for handling the errors
            setFormStatusData({
                message: `Crikey! There was an error processing your request: ${error}`,
                show: true,
                colour: 'red',
            });
        }
    }, [purchaseData.amount]);

    const processServerActionPurchaseData = useCallback(async () => {
        try {
            const { error } = await logPurchaseData(
                purchaseData,
                paymentIntent.paymentIntentId,
            );

            if (error) {
                console.error('Server action failed:', error);
                throw new Error(error);
            }
        } catch (error) {
            console.error('Server action failed:', error);
            // Handle error
            // this is something I am trying, might not work well for handling the errors
            setFormStatusData({
                message: `Crikey! There was an error processing your request: ${error}`,
                show: true,
                colour: 'red',
            });
        }
    }, [purchaseData, paymentIntent.paymentIntentId]);

    /**
     * Object array that contains the purchase flow steps and components they render
     */
    const purchaseStep: {
        key: string; // key to identify the step
        title: string; // title to display
        component: () => JSX.Element; // components to render
    }[] = useMemo(
        () => [
            {
                key: 'giftCardSelection',
                title: 'SELECT AMOUNT',
                component: () => (
                    <GiftCardPurchaseSelection
                        purchaseDataAmount={purchaseData.amount}
                        setPurchaseData={setPurchaseData}
                    />
                ),
            },
            {
                key: 'purchaserDetails',
                title: 'PURCHASER DETAILS',
                component: () => (
                    <PurchaserDetailsComponent
                        purchaseData={purchaseData}
                        setPurchaseData={setPurchaseData}
                    />
                ),
            },
            {
                key: 'recipientDetails',
                title: 'RECIPIENT DETAILS',
                component: () => (
                    <RecipientDetailsComponent
                        recipientDetails={purchaseData.recipientDetails}
                        setPurchaseData={setPurchaseData}
                    />
                ),
            },
            {
                key: 'summary',
                title: 'SUMMARY',
                component: () => (
                    <GiftCardSummary purchaseData={purchaseData} />
                ),
            },
            {
                key: 'stripePayment',
                title: 'PAYMENT DETAILS',
                component: () => (
                    <>
                        <StripeCardElement
                            stripePaymentIntent={paymentIntent.clientSecret}
                        >
                            <StripeCardPurchaseComponent
                                isLoading={isLoading}
                                setIsLoading={setIsLoading}
                                formStatusData={formStatusData}
                                setFormStatusData={setFormStatusData}
                                paymentStatus={paymentStatus}
                                setPaymentStatus={setPaymentStatus}
                                paymentIntentData={paymentIntent}
                                purchaseData={purchaseData}
                                setPurchaseData={setPurchaseData}
                            />
                        </StripeCardElement>
                    </>
                ),
            },
        ],
        [purchaseData, paymentIntent, isLoading, formStatusData, paymentStatus],
    );

    /**
     * Get the index of the current payment flow step
     */
    const getCurrentStepIndex = useCallback(() => {
        return purchaseStep.findIndex((step) => step.key === currentStepKey);
    }, [currentStepKey, purchaseStep]);

    /**
     * Logic to correctly move between steps in the purchase flow
     */
    const handleMoveBetweenSteps = useCallback(
        (direction: boolean) => {
            const currentIndex = getCurrentStepIndex();
            let nextIndex = direction ? currentIndex + 1 : currentIndex - 1;

            // Trigger server action in background when moving from giftCardSelection
            if (
                direction &&
                purchaseStep[currentIndex].key === 'giftCardSelection'
            ) {
                processServerActionPaymentIntent(); // Fire and forget
            }

            // Skip recipientDetails in both directions if gift is false
            if (
                !purchaseData.gift &&
                ((direction &&
                    purchaseStep[nextIndex]?.key === 'recipientDetails') ||
                    (!direction &&
                        purchaseStep[currentIndex]?.key === 'summary'))
            ) {
                nextIndex = direction ? nextIndex + 1 : nextIndex - 1;
            }

            if (direction && currentStepKey === 'summary') {
                processServerActionPurchaseData();
            }

            // check if next step exists, and then move to it if it does exist.
            if (nextIndex >= 0 && nextIndex < purchaseStep.length) {
                setCurrentStepKey(purchaseStep[nextIndex].key);
            }
        },
        [
            getCurrentStepIndex,
            purchaseData.gift,
            purchaseStep,
            processServerActionPaymentIntent,
            processServerActionPurchaseData,
            currentStepKey,
        ],
    );

    const verifyCanMoveStep = useCallback(() => {
        switch (currentStepKey) {
            case 'giftCardSelection':
                return (
                    purchaseData.amount < 20 ||
                    purchaseData.amount > 2500 ||
                    !Number.isInteger(purchaseData.amount)
                );
            case 'purchaserDetails':
                return Object.values(purchaseData.purchaserDetails).some(
                    (value) => value === '',
                );
            case 'recipientDetails':
                return Object.values(purchaseData.recipientDetails!).some(
                    (value) => value === '',
                );
            default:
                return false;
        }
    }, [currentStepKey, purchaseData]);

    const currentStep = useMemo(
        () => purchaseStep.find((step) => step.key === currentStepKey),
        [purchaseStep, currentStepKey],
    );

    return (
        <div className={style.purchaseFlowContainer}>
            {isLoading && <LoadingSpinner active={isLoading} />}

            <>
                <div className={style.stepContainer}>
                    <p className={style.stepTitle}>{currentStep?.title}</p>
                    <hr
                        style={{
                            width: '100%',
                            borderColor: 'var(--color-brown)',
                            margin: '1rem 0',
                        }}
                    />
                    {currentStep?.component()}
                </div>

                <br />

                {!paymentIntent.fromParams && (
                    <div className={style.sectionNavigationContainer}>
                        <Button
                            onClick={() => handleMoveBetweenSteps(false)}
                            disabled={
                                currentStepKey === 'giftCardSelection' ||
                                isLoading
                            }
                        >
                            BACK
                        </Button>
                        <Button
                            onClick={() => handleMoveBetweenSteps(true)}
                            disabled={
                                currentStepKey === 'stripePayment' ||
                                verifyCanMoveStep() ||
                                isLoading
                            }
                        >
                            NEXT
                        </Button>
                    </div>
                )}
                {paymentStatus === 'complete' && (
                    <div className={style.buttonreturn}>
                        <LinkComponent
                            link1={{
                                text: 'PURCHASE ANOTHER GIFT CARD',
                                url: process.env.NEXT_PUBLIC_STRIPE_PAYMENT_RETURN_URL!,
                            }}
                            link2={{
                                text: 'WARRIOR RESTAURANT & BAR',
                                url: 'https://thecrocodilehunterlodge.com.au/dining',
                                target: '_blank',
                            }}
                        />
                    </div>
                )}
            </>
        </div>
    );
};

export default PurchaseFlow;
